import React, { useEffect, useState } from 'react'
import {
  Modal,
  Button,
  Icon,
  Select,
  Input,
  InputNumber,
  Tooltip,
  Progress
} from 'antd'
import { formatVoiceSaveError } from '../../voiceUtils'

const { Option } = Select

const SYNTHESIS_LANGUAGE_OPTIONS = [
  'Arabic',
  'Burmese',
  'Chinese',
  'Danish',
  'Dutch',
  'English',
  'Finnish',
  'French',
  'German',
  'Greek',
  'Hebrew',
  'Hindi',
  'Indonesian',
  'Italian',
  'Japanese',
  'Khmer',
  'Korean',
  'Lao',
  'Malay',
  'Norwegian',
  'Polish',
  'Portuguese',
  'Russian',
  'Spanish',
  'Swahili',
  'Swedish',
  'Tagalog',
  'Thai',
  'Turkish',
  'Vietnamese'
]
const GENDER_OPTIONS = ['female', 'male']
const AGE_OPTIONS = [
  'child',
  'teenager',
  'young adult',
  'mid-20s',
  'mid-30s',
  'middle-aged',
  'mature',
  'elderly'
]
const EMOTION_OPTIONS = [
  'neutral',
  'warm',
  'calm',
  'gentle',
  'cheerful',
  'excited',
  'enthusiastic',
  'serious',
  'confident',
  'dramatic'
]
const PACE_OPTIONS = [
  'very slow',
  'slow',
  'moderate',
  'natural',
  'slightly fast',
  'fast',
  'measured and steady'
]
const DEFAULT_SYNTHESIS_FORM = {
  voice_name: '',
  language: 'English',
  gender: 'female',
  accent: '',
  age: '',
  emotion: 'neutral',
  pace: 'moderate',
  additional_traits: '',
  cfg_value: 2.0,
  inference_timesteps: 10
}

const formatOptionLabel = value => {
  if (typeof value !== 'string') return ''
  const normalized = value.trim()
  if (!normalized) return ''
  return normalized
    .split(/[-_]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const getDefaultSynthesisForm = () => ({ ...DEFAULT_SYNTHESIS_FORM })

const getResolvedOutput = response =>
  response?.output || response?.result || response?.data || response || {}

const getResolvedAudioUrl = (response, resolvedOutput) =>
  resolvedOutput?.audio_url ||
  resolvedOutput?.audioUrl ||
  response?.audio_url ||
  response?.audioUrl ||
  (typeof response === 'string' ? response : '')

const normalizeProgress = value =>
  typeof value === 'number' && !Number.isNaN(value)
    ? Math.max(0, Math.min(100, Math.round(value)))
    : 0

const CreateVoiceModal = ({
  visible,
  onCancel,
  onGenerate,
  onSave,
  taskProgress = 0,
  taskStatus = null,
  taskMessage = ''
}) => {
  const [synthesisForm, setSynthesisForm] = useState(getDefaultSynthesisForm)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [synthesizedPreview, setSynthesizedPreview] = useState(null)
  const [formError, setFormError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)

  const isBusy = isGenerating || isSaving
  const normalizedTaskProgress = normalizeProgress(taskProgress)
  const hasVoiceName = Boolean(synthesisForm.voice_name?.trim())
  const canSave = Boolean(synthesizedPreview?.audioUrl && hasVoiceName)
  const showSavingProgress = isSaving && taskStatus && taskStatus !== 'success'

  useEffect(() => {
    if (!visible) {
      setSynthesisForm(getDefaultSynthesisForm())
      setIsGenerating(false)
      setIsSaving(false)
      setSynthesizedPreview(null)
      setFormError('')
      setSaveError('')
      setSaveSuccess(false)
    }
  }, [visible])

  const updateSynthesisField = (key, value) => {
    setSynthesisForm(prev => ({
      ...prev,
      [key]: value
    }))
    setFormError('')

    if (key === 'voice_name') {
      setSaveError('')
      setSaveSuccess(false)
      return
    }

    if (synthesizedPreview) {
      setSynthesizedPreview(null)
      setSaveError('')
      setSaveSuccess(false)
    }
  }

  const buildSynthesisPayload = () => ({
    voice_name: synthesisForm.voice_name.trim(),
    language: synthesisForm.language.trim(),
    gender: synthesisForm.gender || 'female',
    accent: synthesisForm.accent?.trim() || '',
    age: synthesisForm.age?.trim() || '',
    emotion: synthesisForm.emotion || 'neutral',
    pace: synthesisForm.pace || 'moderate',
    additional_traits: synthesisForm.additional_traits?.trim() || '',
    cfg_value: Number(synthesisForm.cfg_value) || 2.0,
    inference_timesteps: Number(synthesisForm.inference_timesteps) || 10
  })

  const validateSynthesisForm = () => {
    if (!synthesisForm.voice_name?.trim()) {
      return 'Enter a voice name before generating.'
    }

    if (!synthesisForm.language?.trim()) {
      return 'Select a language before generating.'
    }

    return ''
  }

  const handleClose = () => {
    if (isBusy) return
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  const handleGenerate = async () => {
    if (typeof onGenerate !== 'function') return

    const validationError = validateSynthesisForm()
    if (validationError) {
      setFormError(validationError)
      return
    }

    const payload = buildSynthesisPayload()

    try {
      setIsGenerating(true)
      setFormError('')
      setSaveError('')
      setSaveSuccess(false)
      setSynthesizedPreview(null)

      const response = await onGenerate(payload)
      const resolvedOutput = getResolvedOutput(response)
      const audioUrl = getResolvedAudioUrl(response, resolvedOutput)

      if (!audioUrl) {
        setFormError('Voice generated, but no preview audio was returned.')
        return
      }

      setSynthesizedPreview({
        audioUrl,
        description:
          resolvedOutput?.voice_description ||
          resolvedOutput?.description ||
          '',
        sampleText:
          resolvedOutput?.sample_text ||
          resolvedOutput?.sampleText ||
          resolvedOutput?.text ||
          '',
        language: resolvedOutput?.language || payload.language,
        gender: resolvedOutput?.gender || payload.gender
      })
    } catch (error) {
      setFormError(error?.message || 'Failed to create voice.')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSave = async () => {
    if (typeof onSave !== 'function' || !synthesizedPreview?.audioUrl) return

    const voiceName = synthesisForm.voice_name.trim()
    if (!voiceName) {
      setSaveError('Enter a voice name before saving.')
      return
    }

    try {
      setIsSaving(true)
      setSaveError('')
      setSaveSuccess(false)
      await onSave({
        audioUrl: synthesizedPreview.audioUrl,
        voiceName,
        language: synthesizedPreview.language || synthesisForm.language,
        description: synthesizedPreview.description
      })
      setSaveSuccess(true)
    } catch (error) {
      setSaveError(formatVoiceSaveError(error, voiceName))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      maskClosable={!isBusy}
      visible={visible}
      onCancel={handleClose}
      footer={null}
      width={700}
      centered
      destroyOnClose={false}
      className="voice-selection-modal__create-modal"
      title="Create Voice"
    >
      <section className="voice-selection-modal__create-card">
        <div className="voice-selection-modal__create-head">
          <h3>Create a voice</h3>
          <p>Generate a preview, listen to it, then save it to Custom Voices.</p>
        </div>

        <div className="voice-selection-modal__create-grid">
          <label
            className={`voice-selection-modal__create-field voice-selection-modal__create-field--full ${
              saveError ? 'voice-selection-modal__create-field--error' : ''
            }`}
          >
            <span>Voice Name</span>
            <Input
              value={synthesisForm.voice_name}
              onChange={event =>
                updateSynthesisField('voice_name', event.target.value)
              }
              placeholder="e.g. narrator_deep"
              disabled={isBusy}
            />
            {saveError && (
              <div
                className="voice-selection-modal__field-error"
                role="alert"
              >
                <Icon type="exclamation-circle" />
                <span>{saveError}</span>
              </div>
            )}
          </label>

          <label className="voice-selection-modal__create-field">
            <span>Language</span>
            <Select
              showSearch
              value={synthesisForm.language}
              onChange={value => updateSynthesisField('language', value)}
              optionFilterProp="children"
              disabled={isBusy}
            >
              {SYNTHESIS_LANGUAGE_OPTIONS.map(language => (
                <Option key={language} value={language}>
                  <span className="voice-selection-modal__option-label">
                    {formatOptionLabel(language)}
                  </span>
                </Option>
              ))}
            </Select>
          </label>

          <label className="voice-selection-modal__create-field">
            <span>Gender</span>
            <Select
              value={synthesisForm.gender}
              onChange={value => updateSynthesisField('gender', value)}
              disabled={isBusy}
            >
              {GENDER_OPTIONS.map(gender => (
                <Option key={gender} value={gender}>
                  <span className="voice-selection-modal__option-label">
                    {formatOptionLabel(gender)}
                  </span>
                </Option>
              ))}
            </Select>
          </label>

          <label className="voice-selection-modal__create-field">
            <span>Accent</span>
            <Input
              value={synthesisForm.accent}
              onChange={event =>
                updateSynthesisField('accent', event.target.value)
              }
              placeholder="e.g. british, new york, parisian"
              disabled={isBusy}
            />
          </label>

          <label className="voice-selection-modal__create-field">
            <span>Age</span>
            <Select
              showSearch
              value={synthesisForm.age || undefined}
              onChange={value => updateSynthesisField('age', value)}
              placeholder="Select age profile"
              optionFilterProp="children"
              allowClear
              disabled={isBusy}
            >
              {AGE_OPTIONS.map(age => (
                <Option key={age} value={age}>
                  <span className="voice-selection-modal__option-label">
                    {formatOptionLabel(age)}
                  </span>
                </Option>
              ))}
            </Select>
          </label>

          <label className="voice-selection-modal__create-field">
            <span>Emotion</span>
            <Select
              showSearch
              value={synthesisForm.emotion}
              onChange={value => updateSynthesisField('emotion', value)}
              optionFilterProp="children"
              disabled={isBusy}
            >
              {EMOTION_OPTIONS.map(emotion => (
                <Option key={emotion} value={emotion}>
                  <span className="voice-selection-modal__option-label">
                    {formatOptionLabel(emotion)}
                  </span>
                </Option>
              ))}
            </Select>
          </label>

          <label className="voice-selection-modal__create-field">
            <span>Pace</span>
            <Select
              showSearch
              value={synthesisForm.pace}
              onChange={value => updateSynthesisField('pace', value)}
              optionFilterProp="children"
              disabled={isBusy}
            >
              {PACE_OPTIONS.map(pace => (
                <Option key={pace} value={pace}>
                  <span className="voice-selection-modal__option-label">
                    {formatOptionLabel(pace)}
                  </span>
                </Option>
              ))}
            </Select>
          </label>

          <label className="voice-selection-modal__create-field voice-selection-modal__create-field--full">
            <span>Additional Traits</span>
            <Input
              value={synthesisForm.additional_traits}
              onChange={event =>
                updateSynthesisField('additional_traits', event.target.value)
              }
              placeholder="e.g. smooth, deep, conversational"
              disabled={isBusy}
            />
          </label>
        </div>

        <div className="voice-selection-modal__create-grid voice-selection-modal__create-grid--quality">
          <label className="voice-selection-modal__create-field">
            <span>
              CFG Value
              <Tooltip title="Expressiveness control (0.5 to 5.0).">
                <Icon type="question-circle" />
              </Tooltip>
            </span>
            <InputNumber
              min={0.5}
              max={5}
              step={0.1}
              value={synthesisForm.cfg_value}
              onChange={value => updateSynthesisField('cfg_value', value || 2.0)}
              disabled={isBusy}
            />
          </label>

          <label className="voice-selection-modal__create-field">
            <span>
              Inference Timesteps
              <Tooltip title="Quality setting (1 to 50). Higher is slower and often better quality.">
                <Icon type="question-circle" />
              </Tooltip>
            </span>
            <InputNumber
              min={1}
              max={50}
              step={1}
              value={synthesisForm.inference_timesteps}
              onChange={value =>
                updateSynthesisField('inference_timesteps', value || 10)
              }
              disabled={isBusy}
            />
          </label>
        </div>

        {formError && (
          <div className="voice-selection-modal__inline-error" role="alert">
            <Icon type="exclamation-circle" />
            <span>{formError}</span>
          </div>
        )}

        {isGenerating && (
          <div className="voice-selection-modal__generating-state">
            <div className="voice-selection-modal__generating-icon">
              <Icon type="loading" />
            </div>
            <h4>Generating Voice</h4>
            <p>{taskMessage || 'Creating your voice...'}</p>
            <Progress
              percent={normalizedTaskProgress}
              status={taskStatus === 'failed' ? 'exception' : 'active'}
            />
          </div>
        )}

        <div className="voice-selection-modal__create-actions">
          <Button
            className="voice-selection-modal__generate-btn"
            onClick={handleGenerate}
            loading={isGenerating}
            disabled={isSaving}
          >
            {synthesizedPreview ? 'Generate Again' : 'Generate Voice'}
          </Button>
        </div>

        {synthesizedPreview?.audioUrl && (
          <div className="voice-selection-modal__preview-card">
            <div className="voice-selection-modal__preview-head">
              <div>
                <h4>Generated Preview</h4>
                <div className="voice-selection-modal__preview-meta">
                  <span>{formatOptionLabel(synthesizedPreview.language)}</span>
                  <span>{formatOptionLabel(synthesizedPreview.gender)}</span>
                </div>
              </div>
              {saveSuccess && (
                <span className="voice-selection-modal__save-success">
                  <Icon type="check-circle" theme="filled" />
                  Saved
                </span>
              )}
            </div>
            {synthesizedPreview.description && (
              <p>{synthesizedPreview.description}</p>
            )}
            {synthesizedPreview.sampleText && (
              <p className="voice-selection-modal__preview-sample">
                {synthesizedPreview.sampleText}
              </p>
            )}
            <audio controls src={synthesizedPreview.audioUrl} />
            {isSaving && (
              <div className="voice-selection-modal__create-status">
                <span>
                  <Icon type="loading" />{' '}
                  {(showSavingProgress && taskMessage) ||
                    'Saving voice to Custom Voices...'}
                </span>
                {showSavingProgress && (
                  <Progress
                    percent={normalizedTaskProgress}
                    size="small"
                    status={taskStatus === 'failed' ? 'exception' : 'active'}
                  />
                )}
              </div>
            )}
            <div className="voice-selection-modal__preview-actions">
              <Button
                type="primary"
                className="voice-selection-modal__save-btn"
                onClick={handleSave}
                loading={isSaving}
                disabled={!canSave || isGenerating || saveSuccess}
              >
                {saveSuccess ? 'Saved to Custom Voices' : 'Save Voice'}
              </Button>
            </div>
          </div>
        )}
      </section>
    </Modal>
  )
}

export default CreateVoiceModal
